/**
 * Accent- and Turkish-aware matching for the searchable `Select`.
 *
 * cmdk's default filter scores raw code points after `toLowerCase()`, so a
 * Turkish user typing "turkiye" never finds "Türkiye" and "inci" never finds
 * "İnci" (JS lowercases "İ" to "i" + a combining dot). Folding both sides to
 * plain ASCII letters first makes the query a user would actually type work.
 */

/** Letters that carry no combining mark to strip, so NFD alone can't fold them. */
const LIGATURES: Record<string, string> = {
  ı: "i",
  ø: "o",
  ß: "ss",
  æ: "ae",
  œ: "oe",
  đ: "d",
  ð: "d",
  þ: "th",
  ł: "l",
};

export function foldText(input: string): string {
  return (
    input
      .toLowerCase()
      .normalize("NFD")
      // Drop combining marks: ü→u, ş→s, ğ→g, é→e, and the dot left by "İ"→"i̇".
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[ıøßæœđðþł]/g, (c) => LIGATURES[c] ?? c)
  );
}

/** A match right after one of these reads as a word start, not a mid-word hit. */
const WORD_BREAK = /[\s\-–—_/\\(),.:;·]/;

/**
 * Score `haystack` against `query` for cmdk: 0 hides the row, higher sorts
 * first. Every whitespace-separated token must appear (so "inci amb" finds
 * "İnci Ambalaj"), and matches at the start of the text or of a word outrank
 * mid-word ones (so "amb" ranks "Ambalaj" above "Kambiyo").
 */
export function matchScore(haystack: string, query: string): number {
  const q = foldText(query).trim();
  if (q === "") return 1;
  const hay = foldText(haystack);
  const tokens = q.split(/\s+/);
  let total = 0;
  for (const token of tokens) {
    // Score the BEST occurrence, not the first: "united k" must rank "United
    // Kingdom" (k starts a word) above "United States of Amerika" (k mid-word).
    let best = 0;
    for (let at = hay.indexOf(token); at >= 0 && best < 3; at = hay.indexOf(token, at + 1)) {
      const here = at === 0 ? 3 : WORD_BREAK.test(hay[at - 1] ?? "") ? 2 : 1;
      if (here > best) best = here;
    }
    if (best === 0) return 0;
    total += best;
  }
  return total / (tokens.length * 3);
}
